import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import CreateIntegrationPlatform from "./CreateIntegrationPlatform.jsx";
import {useState} from "react";
import ThirdPartyServiceEditForm from "./ThirdPartyServiceEditForm.jsx";
import BrandLogo from "../../../utils/brands/BrandLogo.jsx";
import PageTitle from "../../../components/PageTitle.jsx";
import {isPreview} from "../../../utils/environment.js";
import {useAdSystemBrands, useBrands, useErpBrands, useGoogleDataBrands, useLeadBrands} from "../../../utils/brands/branding.js";
import SupportLink from "../../../components/SupportLink.jsx";
import Button from "../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {useIsAdministrator} from "../../../components/auth/rolesAndPermissions.js";
import {getInitialEshopPlatforms, getIntegrationDisplayName} from "./integrationPickerPresentation.js";

export default function CreateIntegrationChoosePlatform({project}) {

    let {t} = useTranslation();
    let navigate = useNavigate();
    let isAdministrator = useIsAdministrator();

    const [brand, setBrand] = useState(null);

    let adSystemIntegrations = useAdSystemBrands();

    let erpIntegrations = useErpBrands();
    let leadIntegrations = useLeadBrands();
    let googleDataIntegrations = useGoogleDataBrands();
    let brands = useBrands();

    const initialEshopPlatforms = getInitialEshopPlatforms({
        erpBrands: erpIntegrations,
        leadBrands: leadIntegrations,
        shoptetAddonBrand: brands.ShoptetAddon,
        upgatesAddonBrand: brands.UpgatesAddon,
        isAdministrator,
    });

    if (brand !== null) {
        return <NarrowSecureLayout
            title={<div className={'inline-flex gap-3'}>
                <BrandLogo brand={brand} className={'mx-aut my-auto'}/>
                <div className={'mx-aut my-auto'}>{getIntegrationDisplayName(brand)}</div>
            </div>}
            actions={<>
                <Button
                    variant={'outline-primary'}
                    onClick={() => setBrand(null)}
                >
                    <span className={'inline-flex items-center gap-2'}>
                        <FontAwesomeIcon icon={faArrowLeft}/>
                        <span>{t('project.back-to-integrations', {defaultValue: 'Zpět'})}</span>
                    </span>
                </Button>
                {brand.helpArticleId &&
                    <SupportLink
                        articleId={brand.helpArticleId}
                        title={t('support.help-for-integration', {integrationName: brand.name})
                        }/>
                }
            </>}
        >
            <ThirdPartyServiceEditForm project={project} brand={brand} projectIntegration={null}/>
        </NarrowSecureLayout>;
    }

    if (isPreview()) {
        return <NarrowSecureLayout>
            <PageTitle>{t('error.endpoint-not-allowed-on-demo')}</PageTitle>
        </NarrowSecureLayout>;
    }

    if (!project.hasErpIntegration && !project.hasLeadIntegration) {
        return <NarrowSecureLayout hiddenAlertIds={['missing-subscription']}>
            <PageTitle>{t('project.connect-project-data-source', {defaultValue: 'Propojte svůj projekt se zdrojem dat'})}</PageTitle>
            {t('project.choose-data-source-platform', {defaultValue: 'Vyberte platformu, ze které chcete do Datixa načítat data.'})}

            <div className={'flex flex-wrap gap-3 py-6'}>
                {initialEshopPlatforms.map(({brand: platformBrand, href}) => <CreateIntegrationPlatform
                    brand={platformBrand}
                    key={platformBrand.slug}
                    href={href}
                    onClick={href ? undefined : () => setBrand(platformBrand)}
                />)}
            </div>

        </NarrowSecureLayout>;
    }

    return <NarrowSecureLayout
        hiddenAlertIds={['missing-subscription']}
        title={t('project.add-integration', {defaultValue: 'Přidat integraci'})}
        actions={<Button
            variant={'outline-primary'}
            onClick={() => navigate('/project/' + project.id + '/settings/integrations')}
        >
            <span className={'inline-flex items-center gap-2'}>
                <FontAwesomeIcon icon={faArrowLeft}/>
                <span>{t('project.back-to-integrations', {defaultValue: 'Zpět'})}</span>
            </span>
        </Button>}
    >
        <h2 className={'mb-2 mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400'}>
            {t('project.integration-category.marketing', {defaultValue: 'Marketingové integrace'})}
        </h2>
        <div className={'flex flex-wrap gap-3 pb-6'}>
            {adSystemIntegrations.length > 0 ? adSystemIntegrations.map((brand) => {
                return <CreateIntegrationPlatform
                    brand={brand}
                    key={brand.slug}
                    onClick={() => setBrand(brand)}
                />;
            }) : t('project.no-integration-for-this-category')}
        </div>
        {googleDataIntegrations.length > 0 && <>
            <h2 className={'mb-2 mt-4 text-sm font-semibold text-slate-500 dark:text-slate-400'}>
                {t('project.integration-category.data-and-analytics', {defaultValue: 'Datové a analytické integrace'})}
            </h2>
            <div className={'flex flex-wrap gap-3 pb-6'}>
                {googleDataIntegrations.map((brand) => <CreateIntegrationPlatform
                    brand={brand}
                    key={brand.slug}
                    onClick={() => setBrand(brand)}
                />)}
            </div>
        </>}
    </NarrowSecureLayout>;
}
